import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, ArrowRight, ClipboardList, CalendarClock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = 'w-full px-4 py-3 border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40 focus:border-[#00A79D]';

export default function BuyingHelpSurvey() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [prefill, setPrefill] = useState({});
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.title = 'Your homecare assessment - CareTechGuide';
    axios.get(`${API}/public/buying-help/survey/${token}`)
      .then((r) => { setSchema(r.data.schema); setPrefill(r.data.prefill || {}); setDone(!!r.data.completed); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k, v) => setAnswers((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/public/buying-help/survey/${token}`, { answers });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]"><Loader2 className="w-6 h-6 animate-spin text-[#00A79D]" /></div>
  );

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4">
      <div className="text-center bg-white border border-gray-200 rounded-xl p-10 max-w-md" data-testid="survey-notfound">
        <p className="font-semibold text-lg text-[#0B2942]">We couldn’t find this assessment.</p>
        <button onClick={() => navigate('/homecare-buying-help')} className="mt-5 inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-3 rounded-md">
          Start again <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="ctg-public min-h-screen bg-[#F9FAFB]" data-testid="page-buying-help-survey">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center">
          <img src="/brand/caretechcompare-logo.png" alt="Care Tech Compare" className="h-9" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {done ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center" data-testid="survey-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-bold text-3xl text-[#0B2942]">Thank you.</h1>
            <p className="text-gray-600 mt-3 max-w-md mx-auto">
              I’ll review what matters for your service and come back with any clarifying questions before preparing
              your tailored shortlist. Keep an eye on your inbox.
            </p>
            <div className="mt-7 border-t border-gray-100 pt-6">
              <p className="text-sm text-gray-500 mb-3">Prefer to talk it through? Grab 15 minutes with me.</p>
              <a
                href={"mailto:liam@caretechguide.co.uk?subject=" +
                  encodeURIComponent("Book a 15-minute call - homecare system") +
                  "&body=" + encodeURIComponent("Hi Liam,\n\nI've just completed the assessment and I'd like to book a 15-minute call to talk through my homecare system options.\n\nA couple of times that suit me:\n- \n- \n\nThanks")}
                className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3.5 rounded-md transition-colors"
                data-testid="survey-book-call">
                <CalendarClock className="w-4 h-4" /> Book a 15-minute call with Liam
              </a>
            </div>
          </div>
        ) : (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-4">
              <ClipboardList className="w-3.5 h-3.5" /> About 7 minutes
            </span>
            <h1 className="font-bold text-3xl sm:text-4xl text-[#0B2942] leading-tight">{schema.title}</h1>
            <p className="text-gray-600 mt-3">{schema.intro}</p>
            {prefill.name && <p className="text-sm text-gray-500 mt-2">Signed in as <strong>{prefill.name}</strong> ({prefill.email})</p>}

            <form onSubmit={submit} className="mt-8 space-y-8" data-testid="survey-form">
              {schema.sections.map((sec, si) => (
                <div key={sec.heading} className="bg-white border border-gray-200 rounded-xl p-6">
                  <h2 className="font-semibold text-lg text-[#0B2942] mb-4">{sec.heading}</h2>
                  <div className="space-y-5">
                    {sec.fields.map((f) => (
                      <div key={f.key}>
                        <label className="block text-sm font-medium text-gray-800 mb-1.5">
                          {f.label}{!f.optional && <span className="text-red-500"> *</span>}
                        </label>
                        {f.type === 'textarea' ? (
                          <textarea rows={3} className={field} value={answers[f.key] || ''}
                            onChange={(e) => set(f.key, e.target.value)} data-testid={`survey-${f.key}`} required={!f.optional} />
                        ) : f.type === 'choice' ? (
                          <div className="flex flex-wrap gap-2" data-testid={`survey-${f.key}`}>
                            {f.options.map((o) => (
                              <button type="button" key={o} onClick={() => set(f.key, o)}
                                className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${answers[f.key] === o ? 'bg-[#00A79D] border-[#00A79D] text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-[#00A79D]'}`}
                                data-testid={`survey-${f.key}-${o.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}>
                                {o}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <input type="text" className={field} value={answers[f.key] || ''}
                            onChange={(e) => set(f.key, e.target.value)} data-testid={`survey-${f.key}`} required={!f.optional} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button type="submit" disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-4 rounded-md transition-colors disabled:opacity-60"
                data-testid="survey-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Send my answers to Liam
              </button>
              <p className="text-xs text-gray-500 text-center">Thank you for completing this. Next steps - we’ll come back with any clarifying questions before preparing your list.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
